<template>
  <div class="book-cover" :class="`book-cover--${color}`">
    <div class="book-cover__stage">
      <div class="book-cover__book">
        <div class="book-cover__spine" aria-hidden="true">
          <span class="book-cover__spine-light" />
          <span class="book-cover__spine-groove" />
        </div>

        <div class="book-cover__face">
          <div class="book-cover__effects" aria-hidden="true">
            <div class="book-cover__face-shade" />
            <div class="book-cover__face-gloss" />
          </div>

          <div class="book-cover__content">
            <span class="book-cover__logo">{{ logo }}</span>

            <h3 class="book-cover__title">
              <span
                v-for="(line, index) in titleLines"
                :key="index"
                class="book-cover__title-line"
              >
                {{ line }}
              </span>
            </h3>

            <span class="book-cover__publisher">{{ publisher }}</span>
          </div>
        </div>
      </div>

      <div class="book-cover__shadow" aria-hidden="true" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";

const props = defineProps({
  color: {
    type: String,
    required: true,
    validator: (value) => ["blue", "purple"].includes(value),
  },
  title: {
    type: String,
    required: true,
  },
  logo: {
    type: String,
    required: true,
  },
  publisher: {
    type: String,
    required: true,
  },
});

const titleLines = computed(() =>
  props.title.split("\n").map((line) => line.trim()).filter(Boolean)
);
</script>

<style scoped>
.book-cover {
  --book-h: 196px;
  --book-w: 148px;
  --book-r: 15px;
  --book-spine: 30px;
  margin-bottom: 24px;
  direction: ltr; /* شکل کتاب مثل قبل بماند */
}

.book-cover--blue {
  --face-1: #4a6fe8;
  --face-2: #3d5fd4;
  --face-3: #3454c4;
  --spine-1: #3558c0;
  --spine-2: #2a4aad;
  --spine-3: #234696;
}

.book-cover--purple {
  --face-1: #9b5de5;
  --face-2: #8a4fd8;
  --face-3: #7a42cc;
  --spine-1: #6f38be;
  --spine-2: #5f2eae;
  --spine-3: #502696;
}

.book-cover__stage {
  position: relative;
  height: calc(var(--book-h) + 24px);
  perspective: 900px;
  perspective-origin: 50% 42%;
  display: flex;
  align-items: flex-end;
  justify-content: center;
}

.book-cover__book {
  display: flex;
  width: var(--book-w);
  height: var(--book-h);
  margin: 0 auto;
  border-radius: var(--book-r);
  transform: rotateY(-11deg) rotateX(2deg);
  transform-style: preserve-3d;
  transition: transform 0.45s cubic-bezier(0.22, 1, 0.36, 1);
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.32);
  will-change: transform;
  -webkit-font-smoothing: antialiased;
}

/* Spine */
.book-cover__spine {
  position: relative;
  flex: 0 0 var(--book-spine);
  height: 100%;
  border-radius: var(--book-r) 0 0 var(--book-r);
  background: linear-gradient(
    90deg,
    var(--spine-1) 0%,
    var(--spine-2) 55%,
    var(--spine-3) 100%
  );
  box-shadow:
    inset 4px 0 10px rgba(255, 255, 255, 0.16),
    inset -5px 0 12px rgba(0, 0, 0, 0.16);
}

.book-cover__spine-light {
  position: absolute;
  top: 12px;
  bottom: 12px;
  left: 5px;
  width: 7px;
  border-radius: 5px;
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0.35) 0%,
    rgba(255, 255, 255, 0.1) 100%
  );
}

.book-cover__spine-groove {
  position: absolute;
  top: 10px;
  bottom: 10px;
  right: 7px;
  width: 4px;
  border-radius: 2px;
  background: rgba(255, 255, 255, 0.16);
}

/* Front face */
.book-cover__face {
  position: relative;
  flex: 1;
  min-width: 0;
  height: 100%;
  border-radius: 0 var(--book-r) var(--book-r) 0;
  overflow: hidden;
  background: linear-gradient(
    168deg,
    var(--face-1) 0%,
    var(--face-2) 50%,
    var(--face-3) 100%
  );
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.2);
}

.book-cover__effects {
  position: absolute;
  inset: 0;
  z-index: 0;
  pointer-events: none;
}

.book-cover__face-shade {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    90deg,
    rgba(0, 0, 0, 0.12) 0%,
    transparent 22%
  );
}

.book-cover__face-gloss {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    140deg,
    rgba(255, 255, 255, 0.12) 0%,
    transparent 38%
  );
}

/* Content — always above effects */
.book-cover__content {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 13px 9px 12px 11px;
  box-sizing: border-box;
}

.book-cover__logo {
  flex-shrink: 0;
  font-size: 9px;
  font-weight: 600;
  line-height: 1.2;
  color: rgba(255, 255, 255, 0.78);
  letter-spacing: 0.01em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.book-cover__title {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin: 0;
  padding: 5px 0;
  font-size: 13px;
  font-weight: 800;
  line-height: 1.15;
  letter-spacing: 0.01em;
  text-transform: uppercase;
  color: #ffffff;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.18);
}

.book-cover__title-line {
  display: block;
}

.book-cover__publisher {
  flex-shrink: 0;
  font-size: 9px;
  font-weight: 600;
  line-height: 1.2;
  color: rgba(255, 255, 255, 0.75);
  letter-spacing: 0.01em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Ground shadow */
.book-cover__shadow {
  position: absolute;
  bottom: 0;
  left: 50%;
  width: var(--book-w);
  height: 12px;
  transform: translateX(-50%);
  background: radial-gradient(
    ellipse at center,
    rgba(0, 0, 0, 0.34) 0%,
    transparent 70%
  );
  filter: blur(4px);
  pointer-events: none;
  transition:
    opacity 0.45s ease,
    transform 0.45s cubic-bezier(0.22, 1, 0.36, 1);
}
</style>
